// Justificación de tamaño (policies/code.md, "SOLID"/"Component size") -- 646 líneas operativas.
// `ComponentTaskFlow.jsx` es el canvas de roadmap de tareas (nodos arrastrables/conectables +
// barras de tarea sobre una línea de tiempo). Bajó de 1700 a 646 líneas operativas (2026-09-08,
// 14 pasos): helpers puros a `lib/flowCanvas.js`, el motor de mouse/touch/pan/zoom/drag/resize/
// connect a `hooks/useFlowCanvasInteraction.js`, y 10 componentes hoja (`FlowAddNodeMenu`/
// `FlowGanttHeader`/`FlowGridLayer`/`FlowEdgesLayer`/`FlowImageNode`/`FlowLineShape`/
// `FlowTriggerNode`/`FlowEtapaNode`/`FlowTextNode`/`FlowTaskBar`) -- ver los imports de arriba.
// Lo que queda es el container: `useImperativeHandle` (comandos imperativos padre→hijo,
// `focusTask`/`focusNode`), ~15 refs/estado de interacción, `addFlowNode`/`commitDrawingLine`
// (los verbos de creación que cada leaf/menú dispara), y el dispatch de los 6 tipos de nodo +
// barras de tarea a sus componentes ya extraídos. Composición es en sí una responsabilidad
// legítima (mismo criterio que `ComponentTaskHoy.jsx`, que cita este archivo como precedente).
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { toUserZoned, fromUserZoned } from '../../../shared/lib/tzDate';
import { useWindowEvent } from '../../../shared/hooks/useWindowEvent';
import { DAY_WIDTH, CANVAS_HEIGHT, getSmoothPath, getStageColor, getWeekNumber, parseRoadmapNode, resolveNodeRenderPosition } from '../lib/flowCanvas';
import { useFlowCanvasInteraction } from '../hooks/useFlowCanvasInteraction';
import FlowAddNodeMenu from './FlowAddNodeMenu';
import FlowGanttHeader from './FlowGanttHeader';
import FlowGridLayer from './FlowGridLayer';
import FlowEdgesLayer from './FlowEdgesLayer';
import FlowImageNode from './FlowImageNode';
import FlowLineShape from './FlowLineShape';
import FlowTriggerNode from './FlowTriggerNode';
import FlowEtapaNode from './FlowEtapaNode';
import FlowTextNode from './FlowTextNode';
import FlowTaskBar from './FlowTaskBar';
import './ComponentTaskFlow.css';

// slice 02.1 · paso a2b — antes hablaba con `ComponentTaskHoy` por 5 canales `window`
// (`roadmap-focus-task`/`-focus-node`/`-nodes-updated`/`-update-task`/`-resize-task`). Ahora:
// `forwardRef` + `useImperativeHandle` para los comandos imperativos padre→hijo
// (`focusTask`/`focusNode`) y callbacks (`onNodesChange`/`onTaskDrop`/`onTaskResize`) para
// hijo→padre. Cero `window` events.
// Fase 5.17 -- `toast` llega por prop desde `ComponentTaskHoy` (antes `useToast()` directo). Los
// `fetch` crudos de `roadmap_nodes` perdieron el header `Authorization: Bearer <localStorage
// token>` — eran dead weight, todos ya mandan `credentials: 'include'` (cookie httpOnly).
export const ComponentTaskFlow = forwardRef(({ tasks = [], activeTaskId, systemUsers = [], showTriggersMenu = false, onTaskClick, onTaskOpen, spaceId, onNodesChange, onTaskDrop, onTaskResize, userTz = 'America/Mexico_City', toast }, ref) => {
  const { t, i18n } = useTranslation('tasks');

  useEffect(() => {
    if (!spaceId) return;
    fetch(`/api/spaces/${spaceId}/roadmap_nodes`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setFlowNodes(data.map(parseRoadmapNode));
        }
      }).catch(err => console.error('Error fetching roadmap nodes:', err));
  }, [spaceId]);

  // Fase timezones (sevilla-timezones): el "año" del canvas (1 ene - 31 dic) y el punto de
  // referencia para toda la matemática de posición X se calculan en `userTz`, no en el
  // navegador -- antes eran constantes a nivel de módulo (calculadas una sola vez con
  // `new Date()` del navegador al cargar la app).
  const { startDateObj, endDateObj, TOTAL_DAYS, CANVAS_WIDTH } = React.useMemo(() => {
    const year = toUserZoned(new Date(), userTz).getFullYear();
    const start = fromUserZoned(new Date(year, 0, 1), userTz);
    const end = fromUserZoned(new Date(year, 11, 31), userTz);
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return { startDateObj: start, endDateObj: end, TOTAL_DAYS: totalDays, CANVAS_WIDTH: totalDays * DAY_WIDTH };
  }, [userTz]);

  useEffect(() => {
    // Inject hover styles for drag handle
    if (!document.getElementById('task-flow-styles')) {
        const style = document.createElement('style');
        style.id = 'task-flow-styles';
        style.innerHTML = `
            .roadmap-task-node .task-drag-handle { opacity: 0; transition: opacity 0.2s; }
            .roadmap-task-node:hover .task-drag-handle { opacity: 1; }
            .roadmap-task-node .task-resize-handle { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3)); transition: background 0.2s; }
            .roadmap-task-node:hover .task-resize-handle { background: rgba(59, 130, 246, 0.4); }
            .roadmap-task-node .task-resize-handle:hover { background: rgba(59, 130, 246, 0.8) !important; }
        `;
        document.head.appendChild(style);
    }
  }, []);
  const flowContainerRef = useRef(null);

  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);

  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editingUrlNodeId, setEditingUrlNodeId] = useState(null);

  const [drawingLine, setDrawingLine] = useState(null);
  const [liveMousePoint, setLiveMousePoint] = useState(null);
  const drawingLineRef = useRef(null);
  drawingLineRef.current = drawingLine;

  const [copiedNodes, setCopiedNodes] = useState([]);
  const copiedNodesRef = useRef([]);
  copiedNodesRef.current = copiedNodes;
  
  const mousePosRef = useRef({ x: 0, y: 0 });
  const flowNodesRef = useRef([]);
  flowNodesRef.current = flowNodes;

  // Filter tasks that have dates (moved up for group drag calculations)
  const mapTasks = React.useMemo(() => {
    return tasks.filter(t => t.date || t.start_time);
  }, [tasks]);

  const {
    flowPos, setFlowPos, flowScale,
    isPanMode, setIsPanMode,
    selectedNodeIds, setSelectedNodeIds,
    selectionBox,
    groupMove,
    dropTooltip,
    flowDrawingEdge, setFlowDrawingEdge,
    resizingTaskId, resizeWidth,
    flowDragRef,
    clampPosition,
    handleElementMoveStart, handleResizeStart, startNodeResize, endConnect,
    onFlowMouseDown, onFlowMouseMove, onFlowMouseUp, onFlowMouseLeave,
    onFlowTouchStart, onFlowTouchMove, onFlowTouchEnd,
    onFlowDragOver, onFlowDrop,
  } = useFlowCanvasInteraction({
    spaceId, tasks, mapTasks, startDateObj, CANVAS_WIDTH, toast,
    onTaskDrop, onTaskClick, onTaskResize,
    flowNodes, setFlowNodes, setFlowEdges,
    refs: { flowContainerRef, drawingLineRef, mousePosRef },
    setDrawingLine, setLiveMousePoint,
  });

  const selectedNodeIdsRef = useRef([]);
  selectedNodeIdsRef.current = selectedNodeIds;

  const commitDrawingLine = () => {
      const line = drawingLineRef.current;
      if (!line || line.points.length < 2) {
          setDrawingLine(null);
          setLiveMousePoint(null);
          return;
      }
      const id = 'fn_' + Date.now();
      const xs = line.points.map(p => p.x);
      const ys = line.points.map(p => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);
      const w = Math.max(10, maxX - minX);
      const h = Math.max(10, maxY - minY);
      
      const localPoints = line.points.map(p => ({ x: p.x - minX, y: p.y - minY }));
      const contentStr = `L:${JSON.stringify({ pts: localPoints, c: line.colorToken })}`;
      
      const newNode = {
          id, category: 'formas', type: 'formas', sub: 'linea', colorToken: line.colorToken,
          pos_x: minX, pos_y: minY, width: w, height: h, content: contentStr
      };
      
      fetch(`/api/spaces/${spaceId}/roadmap_nodes`, { 
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' }, 
          credentials: 'include', 
          body: JSON.stringify(newNode) 
      }).catch(console.error);
      
      setFlowNodes(prev => [...prev, { ...newNode, x: minX, y: minY, w, h, points: localPoints }]);
      setDrawingLine(null);
      setLiveMousePoint(null);
  };

  // 04-enforce.md 4.3 -- keydown/keyup por la suscripción compartida (`useWindowEvent` lee el
  // handler de un ref, así que las closures ven siempre el estado fresco sin re-suscribir).
  const handleFlowKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.code === 'Space') {
          if (e.code === 'Space') setIsPanMode(true);
      }
      
      // Copy
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
          if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
          const currentSelected = selectedNodeIdsRef.current;
          if (currentSelected.length > 0) {
              const nodesToCopy = flowNodesRef.current.filter(n => currentSelected.includes(n.id));
              if (nodesToCopy.length > 0) {
                  setCopiedNodes(nodesToCopy);
                  toast.success(`${nodesToCopy.length} elemento(s) copiado(s)`);
              }
          }
      }

      // Paste
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
          if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
          const copied = copiedNodesRef.current;
          if (copied.length > 0) {
              const minX = Math.min(...copied.map(n => n.x));
              const minY = Math.min(...copied.map(n => n.y));
              
              const newNodes = copied.map(n => {
                  const id = 'fn_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
                  const offsetX = n.x - minX;
                  const offsetY = n.y - minY;
                  const targetX = mousePosRef.current.x + offsetX;
                  const targetY = mousePosRef.current.y + offsetY;
                  
                  let newContent = n.content;
                  if (n.type === 'texto' || n.category === 'texto') {
                      newContent = `T:${JSON.stringify({ t: n.label, c: n.colorToken, s: n.fontSize, sub: n.sub, url: n.url })}`;
                  } else if (n.category === 'formas' && n.sub === 'linea') {
                      newContent = `L:${JSON.stringify({ pts: n.points, c: n.colorToken })}`;
                  } else if (n.category === 'etapa' || n.type === 'etapa') {
                      newContent = `C:${n.colorToken || 'pink'}|${n.label || ''}`;
                  }
                  
                  return {
                      ...n,
                      id,
                      x: targetX, y: targetY,
                      pos_x: targetX, pos_y: targetY,
                      content: newContent || n.content
                  };
              });

              newNodes.forEach(newNode => {
                  fetch(`/api/spaces/${spaceId}/roadmap_nodes`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                          id: newNode.id,
                          category: newNode.category || newNode.type,
                          type: newNode.type,
                          sub: newNode.sub,
                          pos_x: newNode.pos_x,
                          pos_y: newNode.pos_y,
                          width: newNode.w,
                          height: newNode.h,
                          content: newNode.content,
                          colorToken: newNode.colorToken
                      })
                  }).catch(console.error);
              });
              
              setFlowNodes(prev => [...prev, ...newNodes]);
              setSelectedNodeIds(newNodes.map(n => n.id));
              toast.success(`${newNodes.length} elemento(s) pegado(s)`);
          }
      }

      if (drawingLineRef.current) {
          if (e.key === 'Enter') {
              e.preventDefault();
              commitDrawingLine();
          } else if (e.key === 'Escape') {
              e.preventDefault();
              setDrawingLine(null);
              setLiveMousePoint(null);
          }
      }
    };
  const handleFlowKeyUp = (e) => {
      if (!e.metaKey && !e.ctrlKey && e.code !== 'Space') setIsPanMode(false);
  };
  useWindowEvent('keydown', handleFlowKeyDown);
  useWindowEvent('keyup', handleFlowKeyUp);

  React.useEffect(() => {
    onNodesChange?.(flowNodes);
  }, [flowNodes, onNodesChange]);


  const addFlowNode = (category, item) => {
    if (category === 'formas' && item.sub === 'linea') {
        setDrawingLine({ points: [], colorToken: '#ffffff' });
        toast.info('Haz clic en el mapa para trazar los puntos. Doble clic o Enter para terminar.');
        return;
    }
    const id = 'fn_' + Date.now();
    let node = { id, category, label: item.label || '', icon: item.icon, sub: item.sub };
    if (category === 'etapa') { node.w = 260; node.h = 180; }
    if (category === 'texto') {
        if (item.sub === 'post-it') { node.w = 200; node.h = 200; }
        else if (item.sub === 'texto-enriquecido') { node.w = 200; node.h = 44; }
        else { node.w = 200; node.h = 50; } // texto simple y texto-inverso
        node.colorToken = item.sub === 'post-it' ? 'yellow' : '#ffffff';
        node.fontSize = 16;
        node.url = '';
        node.label = 'Escribe aquí...';
    }
    
    const rect = flowContainerRef.current?.getBoundingClientRect();
    const screenCenter = rect ? rect.width / 2 : 500;
    const x = (-flowPos.x + screenCenter) / flowScale;
    const y = (-flowPos.y + 200) / flowScale;
    let contentStr = node.label;
    if (category === 'texto') {
        contentStr = `T:${JSON.stringify({ t: node.label, c: node.colorToken, s: node.fontSize, sub: node.sub, url: node.url || '' })}`;
    }
    const newNode = { type: category, pos_x: x, pos_y: Math.max(0, y), width: node.w, height: node.h, content: contentStr };
    fetch(`/api/spaces/${spaceId}/roadmap_nodes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ ...newNode, id: node.id }) }).catch(console.error);
    setFlowNodes(prev => [...prev, {...node, x, y: Math.max(0, y)}]);
  };

  // Comandos imperativos padre→hijo (paso a2b — antes `roadmap-focus-task` / `roadmap-focus-node`).
  useImperativeHandle(ref, () => ({
    focusTask: (task, hasDate) => {
      if (hasDate) {
        const tStart = task.start_time ? new Date(task.start_time) : new Date(task.date);
        if (!isNaN(tStart.getTime())) {
          const fraction = (tStart - startDateObj) / (1000 * 60 * 60 * 24);
          const targetX = fraction * DAY_WIDTH;
          const rect = flowContainerRef.current?.getBoundingClientRect();
          const screenCenter = rect ? rect.width / 2 : 500;
          setFlowPos(prev => clampPosition({ x: - (targetX * flowScale) + screenCenter, y: prev.y }, flowScale));
        }
      } else {
        // Tarea sin fecha: se le asigna una en el centro del viewport actual (lo hace el padre
        // vía `onTaskDrop`, igual que un drag-drop).
        const rect = flowContainerRef.current?.getBoundingClientRect();
        const screenCenter = rect ? rect.width / 2 : 500;
        const centerCanvasX = (screenCenter - flowPos.x) / flowScale;
        const daysFromStart = centerCanvasX / DAY_WIDTH;
        const targetDate = new Date(startDateObj.getTime() + daysFromStart * 24 * 60 * 60 * 1000);
        onTaskDrop?.({ task, newDate: targetDate, newY: (Math.abs(flowPos.y) + 200) / flowScale });
      }
    },
    focusNode: (node) => {
      if (node && flowContainerRef.current) {
        const rect = flowContainerRef.current.getBoundingClientRect();
        setFlowPos({ x: (rect.width / 2) - (node.x * flowScale), y: (rect.height / 2) - (node.y * flowScale) });
      }
    },
  }), [flowPos.x, flowPos.y, flowScale, clampPosition, startDateObj, onTaskDrop]);

  const timeline = React.useMemo(() => {
    const today = new Date();
    // `zonedD`/`startOfToday` usan la misma convención "fake-local" (toUserZoned) -- así sus
    // getters/setters locales (.getDate, .setDate) reflejan userTz, y ambos son comparables
    // entre sí vía .getTime() porque comparten el mismo desplazamiento.
    const zonedToday = toUserZoned(today, userTz);
    const startOfToday = new Date(zonedToday);
    startOfToday.setHours(0, 0, 0, 0);

    const days = [];
    const months = [];

    let currentMonthStr = null;
    let monthStartDayIndex = 0;

    for (let i = 0; i < TOTAL_DAYS; i++) {
      const d = toUserZoned(startDateObj, userTz);
      d.setDate(d.getDate() + i);
      
      const dateLocale = i18n.language === 'en' ? 'en-US' : 'es-ES';
      const rawDayName = d.toLocaleDateString(dateLocale, { weekday: 'short' }).toLowerCase();
      const dayName = rawDayName.replace(/\.$/, '');
      const dayNum = String(d.getDate()).padStart(2, '0');
      const monthName = d.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' });
      
      const isToday = d.getTime() === startOfToday.getTime();
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      
      days.push({ index: i, dayName, dayNum, isToday, isWeekend, dateObj: d });

      if (monthName !== currentMonthStr) {
        if (currentMonthStr !== null) {
          months.push({ name: currentMonthStr, start: monthStartDayIndex, length: i - monthStartDayIndex });
        }
        currentMonthStr = monthName;
        monthStartDayIndex = i;
      }
    }
    if (currentMonthStr !== null) {
      months.push({ name: currentMonthStr, start: monthStartDayIndex, length: TOTAL_DAYS - monthStartDayIndex });
    }

    // Calculate exact pixel for current time (red line)
    const msSinceStartOfYear = today - startDateObj;
    const fractionOfYear = msSinceStartOfYear / (1000 * 60 * 60 * 24);
    const redLineX = fractionOfYear * DAY_WIDTH;

    return { days, months, CANVAS_WIDTH, redLineX };
  }, [CANVAS_WIDTH, TOTAL_DAYS, startDateObj, i18n.language, userTz]);
  // Calculate sticky header values based on current scroll

  const activeDayIndex = Math.max(0, Math.min(timeline.days.length - 1, Math.floor((-flowPos.x / flowScale) / timeline.CANVAS_WIDTH * timeline.days.length) || 0));
  // Re-calculate accurately based on absolute pixels
  const rawActiveDayIndex = Math.max(0, Math.min(timeline.days.length - 1, Math.floor((-flowPos.x / flowScale) / 200 /* DAY_WIDTH */)));
  const activeDayObj = timeline.days[rawActiveDayIndex]?.dateObj || new Date();
  const activeMonthStr = activeDayObj.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', { month: 'long', year: 'numeric' }).toUpperCase();
  const activeWeekStr = t('flow.weekLabel', { number: getWeekNumber(activeDayObj) });

  return (
    <div className="op-task-flow-body">
      <div
        className="op-task-flow-viewport"
        style={{ cursor: isPanMode ? (flowDragRef.current ? 'grabbing' : 'grab') : 'default' }}
        ref={flowContainerRef}
        onMouseDown={onFlowMouseDown}
        onMouseMove={onFlowMouseMove}
        onMouseUp={onFlowMouseUp}
        onMouseLeave={onFlowMouseLeave}
        onDoubleClick={(e) => {
            if (drawingLineRef.current) {
                commitDrawingLine();
            }
        }}
        onTouchStart={onFlowTouchStart}
        onTouchMove={onFlowTouchMove}
        onTouchEnd={onFlowTouchEnd}
        onDragOver={onFlowDragOver}
        onDrop={onFlowDrop}
      >
        {/* Sticky Gantt Header */}
        <FlowGanttHeader timeline={timeline} flowPosX={flowPos.x} flowScale={flowScale} activeMonthStr={activeMonthStr} activeWeekStr={activeWeekStr} />

          <div
            className="op-task-flow-canvas"
            style={{
              width: timeline.CANVAS_WIDTH,
              height: CANVAS_HEIGHT,
              transform: `translate(${flowPos.x}px, ${flowPos.y}px) scale(${flowScale})`,
              transformOrigin: '0 0',
            }}
          >
          {/* Dot grid */}
          <FlowGridLayer timeline={timeline} />

          {/* SVG Connection Layer */}
          <FlowEdgesLayer edges={flowEdges} nodes={flowNodes} drawingEdge={flowDrawingEdge} canvasWidth={timeline.CANVAS_WIDTH} canvasHeight={CANVAS_HEIGHT} />

          {/* Flow Nodes Layer */}
          <div className="op-task-flow-interactive-nodes" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
            {flowNodes.map(node => {
                if (node.category === 'trigger') {
                        {(()=>{
                            const { x: renderX, y: renderY, isMoving: isNodeMoving } = resolveNodeRenderPosition(node, groupMove);
                            return (
                                <FlowTriggerNode
                                    key={node.id}
                                    node={node}
                                    x={renderX}
                                    y={renderY}
                                    isMoving={isNodeMoving}
                                    isSelected={selectedNodeIds.includes(node.id)}
                                    onMoveStart={e => handleElementMoveStart(e, node.id)}
                                    onDelete={() => {
                                        toast.prompt(`¿Estás seguro de eliminar este disparador: ${node.label}?`, {
                                            onConfirm: () => {
                                                setFlowNodes(p => p.filter(n => n.id !== node.id));
                                                fetch(`/api/roadmap_nodes/${node.id}`, { method: 'DELETE', credentials: 'include' }).catch(console.error);
                                            }
                                        });
                                    }}
                                    onConnectStart={e => {
                                        const rect = flowContainerRef.current?.getBoundingClientRect();
                                        const sx = (e.clientX - rect.left - flowPos.x) / flowScale;
                                        const sy = (e.clientY - rect.top - flowPos.y) / flowScale;
                                        setFlowDrawingEdge({ sourceId: node.id, type: 'out', startX: sx, startY: sy, endX: sx, endY: sy });
                                    }}
                                    onConnectEnd={e => endConnect(e, node.id, 'in')}
                                />
                            );
                        })()}
                }

                if (node.category === 'etapa' || node.type === 'etapa') {
                    const { x: renderX, y: renderY, isMoving: isNodeMoving } = resolveNodeRenderPosition(node, groupMove);
                    return (
                        <FlowEtapaNode
                            key={node.id}
                            node={node}
                            x={renderX}
                            y={renderY}
                            isMoving={isNodeMoving}
                            isSelected={selectedNodeIds.includes(node.id)}
                            scale={flowScale}
                            onMoveStart={e => handleElementMoveStart(e, node.id)}
                            onResizeStart={e => startNodeResize(e, node.id)}
                            onDelete={() => {
                                toast.prompt(`¿Estás seguro de eliminar la etapa: ${node.label}?`, {
                                    onConfirm: () => {
                                        setFlowNodes(p => p.filter(n => n.id !== node.id));
                                        fetch(`/api/roadmap_nodes/${node.id}`, { method: 'DELETE', credentials: 'include' }).catch(console.error);
                                    }
                                });
                            }}
                            onColorChange={(key) => {
                                setFlowNodes(prev => prev.map(n => n.id === node.id ? { ...n, colorToken: key } : n));
                                fetch(`/api/roadmap_nodes/${node.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ content: `C:${key}|${node.label || ''}` })
                                }).then(res => { if (res.ok) toast.success('Color guardado'); }).catch(console.error);
                            }}
                            onLabelChange={(newLabel) => {
                                setFlowNodes(p => p.map(n => n.id === node.id ? { ...n, label: newLabel, icon: '' } : n));
                                fetch(`/api/roadmap_nodes/${node.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ content: `C:${node.colorToken || 'pink'}|${newLabel}` })
                                })
                                .then(res => {
                                    if (res.ok) toast.success('Etapa guardada correctamente');
                                })
                                .catch(console.error);
                            }}
                            onConnectStart={e => {
                                const rect = flowContainerRef.current?.getBoundingClientRect();
                                const sx = (e.clientX - rect.left - flowPos.x) / flowScale;
                                const sy = (e.clientY - rect.top - flowPos.y) / flowScale;
                                setFlowDrawingEdge({ sourceId: node.id, type: 'out', startX: sx, startY: sy, endX: sx, endY: sy });
                            }}
                            onConnectEnd={e => endConnect(e, node.id, 'in')}
                        />
                    );
                }

                if (node.category === 'texto' || node.type === 'texto') {
                    const { x: renderX, y: renderY, isMoving: isNodeMoving } = resolveNodeRenderPosition(node, groupMove);
                    const isEditing = editingNodeId === node.id;
                    const isEditingUrl = editingUrlNodeId === node.id;
                    return (
                        <FlowTextNode
                            key={node.id}
                            node={node}
                            x={renderX}
                            y={renderY}
                            isMoving={isNodeMoving}
                            isSelected={selectedNodeIds.includes(node.id)}
                            scale={flowScale}
                            isEditing={isEditing}
                            isEditingUrl={isEditingUrl}
                            onMoveStart={e => handleElementMoveStart(e, node.id)}
                            onResizeStart={e => startNodeResize(e, node.id)}
                            onStartEdit={() => setEditingNodeId(node.id)}
                            onToggleEditUrl={() => setEditingUrlNodeId(editingUrlNodeId === node.id ? null : node.id)}
                            onCloseEditUrl={() => setEditingUrlNodeId(null)}
                            onDelete={() => {
                                toast.prompt(`¿Estás seguro de eliminar este texto?`, {
                                    onConfirm: () => {
                                        setFlowNodes(p => p.filter(n => n.id !== node.id));
                                        fetch(`/api/roadmap_nodes/${node.id}`, { method: 'DELETE', credentials: 'include' }).catch(console.error);
                                    }
                                });
                            }}
                            onColorChange={(colorVal) => {
                                setFlowNodes(prev => prev.map(n => n.id === node.id ? { ...n, colorToken: colorVal } : n));
                                fetch(`/api/roadmap_nodes/${node.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ content: `T:${JSON.stringify({ t: node.label, c: colorVal, s: node.fontSize, sub: node.sub, url: node.url || '' })}` })
                                }).then(res => { if (res.ok) toast.success('Color guardado'); }).catch(console.error);
                            }}
                            onFontSizeChange={(size) => {
                                setFlowNodes(prev => prev.map(n => n.id === node.id ? { ...n, fontSize: size } : n));
                                fetch(`/api/roadmap_nodes/${node.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ content: `T:${JSON.stringify({ t: node.label, c: node.colorToken, s: size, sub: node.sub, url: node.url || '' })}` })
                                }).then(res => { if (res.ok) toast.success('Tamaño guardado'); }).catch(console.error);
                            }}
                            onLabelChange={(newLabel) => {
                                setEditingNodeId(null);
                                if (newLabel !== node.label) {
                                    setFlowNodes(p => p.map(n => n.id === node.id ? { ...n, label: newLabel } : n));
                                    fetch(`/api/roadmap_nodes/${node.id}`, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json' },
                                        credentials: 'include',
                                        body: JSON.stringify({ content: `T:${JSON.stringify({ t: newLabel, c: node.colorToken, s: node.fontSize, sub: node.sub, url: node.url || '' })}` })
                                    })
                                    .then(res => {
                                        if (res.ok) toast.success('Texto guardado correctamente');
                                    })
                                    .catch(console.error);
                                }
                            }}
                            onUrlChange={(url) => {
                                setEditingUrlNodeId(null);
                                setFlowNodes(prev => prev.map(n => n.id === node.id ? { ...n, url } : n));
                                fetch(`/api/roadmap_nodes/${node.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ content: `T:${JSON.stringify({ t: node.label, c: node.colorToken, s: node.fontSize, sub: node.sub, url })}` })
                                }).then(res => { if (res.ok) toast.success('Enlace guardado'); }).catch(console.error);
                            }}
                        />
                    );
                }
                if (node.type === 'image') {
                    const { x: renderX, y: renderY, isMoving: isNodeMoving } = resolveNodeRenderPosition(node, groupMove);
                    return (
                        <FlowImageNode
                            key={node.id}
                            node={node}
                            x={renderX}
                            y={renderY}
                            isMoving={isNodeMoving}
                            isSelected={selectedNodeIds.includes(node.id)}
                            onMoveStart={e => handleElementMoveStart(e, node.id)}
                            onResizeStart={e => startNodeResize(e, node.id)}
                            onDelete={() => {
                                toast.prompt('¿Estás seguro de eliminar esta imagen?', {
                                    onConfirm: () => {
                                        setFlowNodes(p => p.filter(n => n.id !== node.id));
                                        fetch(`/api/roadmap_nodes/${node.id}`, { method: 'DELETE', credentials: 'include' }).catch(console.error);
                                    }
                                });
                            }}
                        />
                    );
                }
                if (node.category === 'formas' && node.sub === 'linea') {
                    const { x: renderX, y: renderY, isMoving: isNodeMoving } = resolveNodeRenderPosition(node, groupMove);
                    return (
                        <FlowLineShape
                            key={node.id}
                            node={node}
                            x={renderX}
                            y={renderY}
                            isMoving={isNodeMoving}
                            isSelected={selectedNodeIds.includes(node.id)}
                            onMoveStart={e => handleElementMoveStart(e, node.id)}
                            onDelete={() => {
                                toast.prompt('¿Estás seguro de eliminar esta línea?', {
                                    onConfirm: () => {
                                        setFlowNodes(p => p.filter(n => n.id !== node.id));
                                        fetch(`/api/roadmap_nodes/${node.id}`, { method: 'DELETE', credentials: 'include' }).catch(console.error);
                                    }
                                });
                            }}
                            onColorChange={(c) => {
                                setFlowNodes(prev => prev.map(n => n.id === node.id ? { ...n, colorToken: c } : n));
                                fetch(`/api/roadmap_nodes/${node.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ content: `L:${JSON.stringify({ pts: node.points, c })}` })
                                }).then(res => { if (res.ok) toast.success('Color guardado'); }).catch(console.error);
                            }}
                        />
                    );
                }
                return null;
            })}
            {/* Active Drawing Line Overlay */}
            {drawingLine && (
                <svg style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible', pointerEvents: 'none', zIndex: 100 }}>
                    <path d={getSmoothPath([...drawingLine.points, ...(liveMousePoint ? [liveMousePoint] : [])])} fill="none" stroke={drawingLine.colorToken} strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" />
                    {drawingLine.points.map((p, i) => (
                        <circle key={i} cx={p.x} cy={p.y} r={4} fill="#fff" stroke="#ec4899" strokeWidth={2} />
                    ))}
                </svg>
            )}

          </div>

          {/* Node Render Layer */}
          <div className="op-task-flow-nodes">
            {mapTasks.map((task, idx) => {
                const tStart = task.start_time ? new Date(task.start_time) : new Date(task.date);
                if (isNaN(tStart.getTime())) return null;

                const startFraction = (tStart - startDateObj) / (1000 * 60 * 60 * 24);
                const startX = startFraction * DAY_WIDTH;

                let endX = startX + DAY_WIDTH; // Default 1 day duration
                if (task.end_time) {
                    const tEnd = new Date(task.end_time);
                    if (!isNaN(tEnd.getTime())) {
                        const endFraction = (tEnd - startDateObj) / (1000 * 60 * 60 * 24);
                        endX = Math.max(startX + 12, endFraction * DAY_WIDTH);
                    }
                }

                const baseWidth = Math.max(12, endX - startX);
                const width = resizingTaskId === task.id ? resizeWidth : baseWidth;
                const color = getStageColor(task.stage);

                // Temporary simple Y distribution if no saved Y
                const originalY = (task.roadmap_y != null) ? Number(task.roadmap_y) : (100 + (idx * 60) % 800);
                const { x: renderX, y: renderY, isMoving } = resolveNodeRenderPosition({ id: task.id, x: startX, y: originalY }, groupMove);
                const assigneeStr = typeof task.assignee === 'string' ? task.assignee.split(',')[0].trim() : (task.assignee?.nick || '?');
                const assigneeUser = systemUsers.find(u => u.nick === assigneeStr || u.name === assigneeStr) || {};

                return (
                    <FlowTaskBar
                        key={task.id}
                        task={task}
                        x={renderX}
                        y={renderY}
                        isMoving={isMoving}
                        width={width}
                        color={color}
                        isActive={task.id === activeTaskId}
                        isSelected={selectedNodeIds.includes(task.id)}
                        assigneeUser={assigneeUser}
                        dropTooltipDate={dropTooltip?.id === task.id ? dropTooltip.dateObj : null}
                        onMoveStart={e => handleElementMoveStart(e, task.id)}
                        onResizeStart={e => handleResizeStart(e, task, width)}
                        onOpen={e => onTaskOpen?.(task, e)}
                    />
                );
            })}
          </div>
          
          {/* Selection Box Render Layer */}
          {selectionBox && (
            <div style={{
              position: 'absolute',
              left: Math.min(selectionBox.startX, selectionBox.currentX),
              top: Math.min(selectionBox.startY, selectionBox.currentY),
              width: Math.abs(selectionBox.currentX - selectionBox.startX),
              height: Math.abs(selectionBox.currentY - selectionBox.startY),
              backgroundColor: 'rgba(13, 153, 255, 0.1)',
              border: '1px solid #0d99ff',
              pointerEvents: 'none',
              zIndex: 9999
            }} />
          )}

        </div>

        {/* Floating Triggers Menu */}
        <FlowAddNodeMenu visible={showTriggersMenu} onAddNode={addFlowNode} />

      </div>
    </div>
  );
});

ComponentTaskFlow.displayName = 'ComponentTaskFlow';
